import * as THREE from "three";
import { mulberry32 } from "../core/rng";
import { AIR, GRASS, isSolid, WATER, WHITE_WOOL } from "../world/blocks";
import { WORLD_HEIGHT } from "../world/chunk";
import { LEATHER, RAW_BEEF, RAW_MUTTON, RAW_PORKCHOP, type ItemStack } from "../world/items";
import type { BlockView } from "./dropped-items";
import {
  isMobLoaded,
  mobWaterInfo,
  MOB_GRAVITY,
  MOB_JUMP_SPEED,
  MOB_WATER_JUMP_SPEED,
  MOB_MAX_STEP,
  MOB_TERMINAL_VELOCITY,
  moveMob,
} from "./mob-physics";

/**
 * Passive animal simulation.
 *
 * Like dropped items, this module is deliberately renderer-free. It owns the
 * animals, their small wander/panic state machines and voxel collision physics,
 * while the render layer only mirrors the resulting transforms. Animals freeze
 * in unloaded chunks so walking away can never make one fall through terrain
 * that no longer exists in memory.
 */

export const ANIMAL_KINDS = ["cow", "pig", "sheep"] as const;
export type AnimalKind = (typeof ANIMAL_KINDS)[number];

/** Population cap; keeps the small procedural herds from filling the scene. */
export const MAX_ANIMALS = 16;
/** Seconds between natural herd-spawn attempts. */
export const ANIMAL_SPAWN_INTERVAL = 6;
/** Natural herds appear between these distances from the player. */
export const ANIMAL_SPAWN_MIN_DISTANCE = 14;
export const ANIMAL_SPAWN_MAX_DISTANCE = 44;
/** Animals farther than this from the player are quietly culled. */
export const ANIMAL_DESPAWN_DISTANCE = 128;
export const ANIMAL_HERD_MIN = 2;
export const ANIMAL_HERD_MAX = 4;
export const ANIMAL_HIT_FLASH_SECONDS = 0.3;

export type AnimalState = "idle" | "wander" | "panic";

export interface AnimalDrop {
  readonly id: number;
  readonly min: number;
  readonly max: number;
}

export interface AnimalDef {
  readonly kind: AnimalKind;
  readonly name: string;
  readonly maxHealth: number;
  /** Full width of the collision box. */
  readonly width: number;
  readonly height: number;
  readonly walkSpeed: number;
  readonly panicSpeed: number;
  readonly drops: readonly AnimalDrop[];
}

export const ANIMAL_DEFS: Readonly<Record<AnimalKind, AnimalDef>> = {
  cow: {
    kind: "cow",
    name: "Cow",
    maxHealth: 10,
    width: 0.84,
    height: 1.42,
    walkSpeed: 1.15,
    panicSpeed: 3.6,
    drops: [
      { id: RAW_BEEF, min: 1, max: 3 },
      { id: LEATHER, min: 0, max: 2 },
    ],
  },
  pig: {
    kind: "pig",
    name: "Pig",
    maxHealth: 8,
    width: 0.72,
    height: 1.05,
    walkSpeed: 1.25,
    panicSpeed: 3.8,
    drops: [{ id: RAW_PORKCHOP, min: 1, max: 2 }],
  },
  sheep: {
    kind: "sheep",
    name: "Sheep",
    maxHealth: 8,
    width: 0.84,
    height: 1.38,
    walkSpeed: 1.1,
    panicSpeed: 3.5,
    drops: [
      { id: RAW_MUTTON, min: 1, max: 2 },
      { id: WHITE_WOOL, min: 1, max: 2 },
    ],
  },
};

export function isAnimalKind(value: unknown): value is AnimalKind {
  return typeof value === "string" && (ANIMAL_KINDS as readonly string[]).includes(value);
}

export function animalDef(kind: AnimalKind): AnimalDef {
  return ANIMAL_DEFS[kind];
}

export function animalMaxHealth(kind: AnimalKind): number {
  return ANIMAL_DEFS[kind].maxHealth;
}

export interface Animal {
  readonly id: number;
  readonly kind: AnimalKind;
  readonly position: THREE.Vector3;
  readonly velocity: THREE.Vector3;
  health: number;
  /** Seconds alive, measured only while loaded and active. */
  age: number;
  yaw: number;
  state: AnimalState;
  aiTimer: number;
  panicTimer: number;
  heading: number;
  jumpCooldown: number;
  hurtFlash: number;
  onGround: boolean;
  /** Set for one tick when horizontal movement hit a solid block. */
  blocked: boolean;
}

/** Renderer-independent save payload; structurally matches SavedAnimal. */
export interface AnimalSave {
  kind: AnimalKind;
  x: number;
  y: number;
  z: number;
  health: number;
  age: number;
}

export interface AnimalHit {
  animal: Animal;
  distance: number;
}

export interface AnimalsOptions {
  /** Tests can turn this off when they want to place every animal by hand. */
  naturalSpawning?: boolean;
}

/** The slice of World used by the animal simulation. */
export type AnimalWorld = BlockView;

export class Animals {
  readonly animals: Animal[] = [];

  private nextId = 1;
  private readonly rand: () => number;
  private readonly naturalSpawning: boolean;
  private spawnTimer = 1.5;
  private readonly ray = new THREE.Ray();
  private readonly rayDirection = new THREE.Vector3();
  private readonly box = new THREE.Box3();
  private readonly boxPoint = new THREE.Vector3();

  constructor(
    private readonly world: AnimalWorld,
    seed = 1,
    options: AnimalsOptions = {},
  ) {
    // Offset the stream so animals never line up with dropped-item randomness.
    this.rand = mulberry32((seed ^ 0x6d2b79f5) >>> 0);
    this.naturalSpawning = options.naturalSpawning ?? true;
  }

  /** Add one animal. Returns null at the population cap or on invalid input. */
  spawn(
    kind: AnimalKind,
    x: number,
    y: number,
    z: number,
    options: { health?: number; age?: number; yaw?: number } = {},
  ): Animal | null {
    if (this.animals.length >= MAX_ANIMALS) return null;
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return null;
    if (y < 0 || y > WORLD_HEIGHT + 16) return null;

    const def = ANIMAL_DEFS[kind];
    const requestedHealth = options.health ?? def.maxHealth;
    if (!Number.isFinite(requestedHealth) || requestedHealth <= 0) return null;
    const health = Math.min(def.maxHealth, requestedHealth);
    const age = Number.isFinite(options.age) && options.age! > 0 ? options.age! : 0;
    const yaw =
      Number.isFinite(options.yaw) && options.yaw !== undefined
        ? options.yaw
        : this.rand() * Math.PI * 2;

    const animal: Animal = {
      id: this.nextId++,
      kind,
      position: new THREE.Vector3(x, y, z),
      velocity: new THREE.Vector3(),
      health,
      age,
      yaw,
      state: "idle",
      aiTimer: 0.4 + this.rand() * 2.2,
      panicTimer: 0,
      heading: yaw,
      jumpCooldown: 0,
      hurtFlash: 0,
      onGround: false,
      blocked: false,
    };
    this.animals.push(animal);
    return animal;
  }

  /**
   * Spawn a small herd on grass around a column. The centre must be grass; all
   * offsets are independently validated, so a herd can be smaller near trees
   * or water without rejecting the whole group.
   */
  spawnHerd(centerX: number, centerZ: number, kind?: AnimalKind): Animal[] {
    const cx = Math.floor(centerX);
    const cz = Math.floor(centerZ);
    const centerY = this.findGrassColumn(cx, cz);
    if (centerY === null || this.animals.length >= MAX_ANIMALS) return [];

    const herdKind = kind ?? this.randomKind();
    const size =
      ANIMAL_HERD_MIN + Math.floor(this.rand() * (ANIMAL_HERD_MAX - ANIMAL_HERD_MIN + 1));
    const created: Animal[] = [];

    for (let i = 0; i < size; i++) {
      if (this.animals.length >= MAX_ANIMALS) break;
      const ox = i === 0 ? 0 : (this.rand() * 2 - 1) * 2.6;
      const oz = i === 0 ? 0 : (this.rand() * 2 - 1) * 2.6;
      const x = i === 0 ? cx : Math.floor(centerX + ox);
      const z = i === 0 ? cz : Math.floor(centerZ + oz);
      const y = i === 0 ? centerY : this.findGrassColumn(x, z);
      if (y === null) continue;
      const animal = this.spawn(herdKind, x + 0.5, y, z + 0.5);
      if (animal) created.push(animal);
    }

    return created;
  }

  /**
   * Advance AI, spawning, cleanup and physics. `dt` is subdivided so a long
   * frame cannot tunnel an animal through the ground.
   */
  update(dt: number, playerPosition: THREE.Vector3): void {
    if (!Number.isFinite(dt) || dt <= 0) return;
    if (!Number.isFinite(playerPosition.x) || !Number.isFinite(playerPosition.z)) return;

    let remaining = Math.min(dt, 0.5);
    while (remaining > 0) {
      const step = Math.min(remaining, MOB_MAX_STEP);
      this.step(step, playerPosition);
      remaining -= step;
    }
  }

  /**
   * Ray/AABB test against every loaded animal. The game compares the returned
   * distance with its block raycast so a cow behind a wall cannot be hit.
   */
  raycast(
    origin: THREE.Vector3,
    direction: THREE.Vector3,
    maxDistance: number,
  ): AnimalHit | null {
    if (!Number.isFinite(maxDistance) || maxDistance <= 0) return null;
    if (direction.lengthSq() < 1e-12) return null;

    this.rayDirection.copy(direction).normalize();
    this.ray.set(origin, this.rayDirection);

    let best: Animal | null = null;
    let bestDistance = maxDistance;

    for (const animal of this.animals) {
      if (!this.isLoaded(animal.position.x, animal.position.z)) continue;
      const def = ANIMAL_DEFS[animal.kind];
      const half = def.width / 2;
      this.box.min.set(
        animal.position.x - half,
        animal.position.y,
        animal.position.z - half,
      );
      this.box.max.set(
        animal.position.x + half,
        animal.position.y + def.height,
        animal.position.z + half,
      );
      const hit = this.ray.intersectBox(this.box, this.boxPoint);
      if (!hit) continue;
      const distance = origin.distanceTo(hit);
      if (distance <= bestDistance) {
        best = animal;
        bestDistance = distance;
      }
    }

    return best ? { animal: best, distance: bestDistance } : null;
  }

  /**
   * Apply melee damage and knockback. Returns true when the animal was killed;
   * the caller is responsible for spawning its drops.
   */
  damage(animal: Animal, amount: number, source: THREE.Vector3): boolean {
    const index = this.animals.indexOf(animal);
    if (index < 0 || !Number.isFinite(amount) || amount <= 0) return false;

    animal.health = Math.max(0, animal.health - amount);
    animal.hurtFlash = ANIMAL_HIT_FLASH_SECONDS;
    if (animal.health <= 0) {
      this.animals.splice(index, 1);
      return true;
    }

    animal.panicTimer = 4.5;
    animal.state = "panic";
    animal.aiTimer = 0;
    animal.onGround = false;

    const dx = animal.position.x - source.x;
    const dz = animal.position.z - source.z;
    const length = Math.hypot(dx, dz);
    if (length > 1e-4) {
      animal.heading = Math.atan2(dx, dz);
      animal.velocity.x += (dx / length) * 5.2;
      animal.velocity.z += (dz / length) * 5.2;
    } else {
      animal.heading = this.rand() * Math.PI * 2;
      animal.velocity.x += Math.sin(animal.heading) * 4.2;
      animal.velocity.z += Math.cos(animal.heading) * 4.2;
    }
    animal.velocity.y = Math.max(animal.velocity.y, 5.1);
    return false;
  }

  /** Loot rolls are part of the simulation so they stay deterministic in tests. */
  rollDrops(kind: AnimalKind): ItemStack[] {
    const drops: ItemStack[] = [];
    for (const drop of ANIMAL_DEFS[kind].drops) {
      const span = drop.max - drop.min + 1;
      const count = drop.min + Math.floor(this.rand() * span);
      if (count > 0) drops.push({ id: drop.id, count });
    }
    return drops;
  }

  /** Snapshot the fields the save format needs. */
  snapshot(): AnimalSave[] {
    return this.animals.map((animal) => ({
      kind: animal.kind,
      x: animal.position.x,
      y: animal.position.y,
      z: animal.position.z,
      health: animal.health,
      age: animal.age,
    }));
  }

  restore(saved: readonly AnimalSave[]): void {
    this.clear();
    for (const entry of saved) {
      if (this.animals.length >= MAX_ANIMALS) break;
      if (!isAnimalKind(entry.kind)) continue;
      this.spawn(entry.kind, entry.x, entry.y, entry.z, {
        health: entry.health,
        age: entry.age,
      });
    }
  }

  clear(): void {
    this.animals.length = 0;
    this.nextId = 1;
    this.spawnTimer = 1.5;
  }

  private step(dt: number, playerPosition: THREE.Vector3): void {
    if (this.naturalSpawning) {
      this.spawnTimer -= dt;
      if (this.spawnTimer <= 0) {
        this.spawnTimer = ANIMAL_SPAWN_INTERVAL;
        if (this.animals.length < MAX_ANIMALS) this.tryNaturalSpawn(playerPosition);
      }
    }

    for (let i = this.animals.length - 1; i >= 0; i--) {
      const animal = this.animals[i];
      const dx = animal.position.x - playerPosition.x;
      const dz = animal.position.z - playerPosition.z;
      if (dx * dx + dz * dz > ANIMAL_DESPAWN_DISTANCE * ANIMAL_DESPAWN_DISTANCE) {
        this.animals.splice(i, 1);
        continue;
      }

      // Chunks outside the render disc are frozen: no aging, falling or AI.
      if (!this.isLoaded(animal.position.x, animal.position.z)) continue;

      animal.age += dt;
      animal.hurtFlash = Math.max(0, animal.hurtFlash - dt);
      animal.jumpCooldown = Math.max(0, animal.jumpCooldown - dt);
      this.updateAi(animal, dt, playerPosition);
      this.applyPhysics(animal, dt);
      this.move(animal, dt);

      if (animal.position.y < -8) this.animals.splice(i, 1);
    }
  }

  private tryNaturalSpawn(playerPosition: THREE.Vector3): void {
    // A few candidates per interval makes water and forests fail gracefully
    // without waiting another full interval for every bad landing spot.
    for (let attempt = 0; attempt < 4 && this.animals.length < MAX_ANIMALS; attempt++) {
      const angle = this.rand() * Math.PI * 2;
      const distance =
        ANIMAL_SPAWN_MIN_DISTANCE +
        this.rand() * (ANIMAL_SPAWN_MAX_DISTANCE - ANIMAL_SPAWN_MIN_DISTANCE);
      const x = Math.floor(playerPosition.x + Math.cos(angle) * distance);
      const z = Math.floor(playerPosition.z + Math.sin(angle) * distance);
      if (!this.isLoaded(x, z)) continue;
      if (this.spawnHerd(x, z).length > 0) return;
    }
  }

  private updateAi(animal: Animal, dt: number, playerPosition: THREE.Vector3): void {
    const def = ANIMAL_DEFS[animal.kind];
    const inWater = mobWaterInfo(this.world, animal.position, def.height) !== null;

    if (animal.panicTimer > 0) {
      animal.panicTimer = Math.max(0, animal.panicTimer - dt);
      animal.state = "panic";
      const dx = animal.position.x - playerPosition.x;
      const dz = animal.position.z - playerPosition.z;
      if (dx * dx + dz * dz > 1e-4) animal.heading = Math.atan2(dx, dz);
    } else if (inWater) {
      // In water: animals continuously paddle and seek shore
      animal.state = "wander";
      animal.aiTimer -= dt;
      if (animal.aiTimer <= 0) {
        const shore = this.findNearbyShore(animal);
        if (shore !== null) {
          animal.heading = shore;
          animal.aiTimer = 2.5 + this.rand() * 2.0;
        } else {
          animal.heading = this.rand() * Math.PI * 2;
          animal.aiTimer = 2.5 + this.rand() * 3.0;
        }
      } else if (this.shouldTurn(animal, true)) {
        animal.heading = this.rand() * Math.PI * 2;
        animal.aiTimer = 2.0 + this.rand() * 2.0;
      }
    } else {
      // On land: wander / idle cycle
      if (animal.state === "panic") {
        animal.state = "idle";
        animal.aiTimer = 0.5 + this.rand();
      }
      animal.aiTimer -= dt;
      if (animal.aiTimer <= 0) {
        if (this.rand() < 0.35) {
          animal.state = "idle";
          animal.aiTimer = 1 + this.rand() * 2.6;
        } else {
          animal.state = "wander";
          animal.heading = this.rand() * Math.PI * 2;
          animal.aiTimer = 2 + this.rand() * 3.5;
        }
      } else if (animal.state === "wander" && animal.onGround && this.shouldTurn(animal, false)) {
        animal.heading = this.rand() * Math.PI * 2;
        animal.aiTimer = 1.5 + this.rand() * 2;
      }
    }

    const speed =
      animal.state === "panic"
        ? def.panicSpeed * (inWater ? 0.7 : 1)
        : animal.state === "wander"
          ? def.walkSpeed * (inWater ? 0.7 : 1)
          : 0;

    const targetX = Math.sin(animal.heading) * speed;
    const targetZ = Math.cos(animal.heading) * speed;
    const blend = Math.min(1, dt * (animal.state === "panic" ? 11 : 7));
    animal.velocity.x += (targetX - animal.velocity.x) * blend;
    animal.velocity.z += (targetZ - animal.velocity.z) * blend;
    if (speed > 0.01) animal.yaw = animal.heading;
  }

  /** Look one block ahead for obstacles (or water/cliffs if on land). */
  private shouldTurn(animal: Animal, inWater = false): boolean {
    const aheadX = animal.position.x + Math.sin(animal.heading) * 0.95;
    const aheadZ = animal.position.z + Math.cos(animal.heading) * 0.95;
    if (!this.isLoaded(aheadX, aheadZ)) return true;

    const def = ANIMAL_DEFS[animal.kind];
    const x = Math.floor(aheadX);
    const z = Math.floor(aheadZ);
    const feetY = Math.floor(animal.position.y + 0.05);
    const headY = Math.floor(animal.position.y + def.height - 0.05);

    if (inWater) {
      // In water: only turn if blocked by a solid wall 2+ blocks high above feet level
      for (let y = feetY + 2; y <= headY + 1; y++) {
        if (isSolid(this.world.peekBlock(x, y, z))) return true;
      }
      return false;
    }

    // On land: avoid water, walls and cliffs
    for (let y = feetY; y <= headY; y++) {
      const block = this.world.peekBlock(x, y, z);
      if (block === WATER) return true;
      if (isSolid(block)) return true;
    }

    let ground = false;
    for (let y = feetY - 1; y >= feetY - 3; y--) {
      const block = this.world.peekBlock(x, y, z);
      if (block === WATER) return true;
      if (isSolid(block)) {
        ground = true;
        break;
      }
    }
    return !ground;
  }

  private applyPhysics(animal: Animal, dt: number): void {
    const def = ANIMAL_DEFS[animal.kind];
    const water = mobWaterInfo(this.world, animal.position, def.height);

    if (water) {
      if (animal.velocity.y > 1.2) {
        // Jumping / leaping out of water onto land: apply standard gravity
        animal.velocity.y -= MOB_GRAVITY * dt;
        return;
      }

      // In real Minecraft, animals float submerged up to their chest (~45% of height).
      // Their legs stay underwater while their heads and upper body stay above water.
      const submersion = Math.min(0.55, def.height * 0.45);
      const targetY = water.surfaceY - submersion;
      const diff = targetY - animal.position.y;

      // Smooth critically damped vertical tracking toward target floating level
      const targetVy =
        Math.max(-1.5, Math.min(1.2, diff * 5.0)) +
        Math.sin(animal.age * 3.0 + animal.id * 1.5) * 0.1;
      animal.velocity.y += (targetVy - animal.velocity.y) * Math.min(1, dt * 8.0);
      return;
    }

    animal.velocity.y -= MOB_GRAVITY * dt;
    if (animal.velocity.y < -MOB_TERMINAL_VELOCITY) {
      animal.velocity.y = -MOB_TERMINAL_VELOCITY;
    }

    // Keep animals from wandering into deep water or off ledges at home.
    if (animal.onGround && animal.state === "wander" && def.walkSpeed > 0) {
      const drag = Math.max(0, 1 - dt * 1.2);
      animal.velocity.x *= drag;
      animal.velocity.z *= drag;
    }
  }

  /** Move through the shared mob AABB sweep, then hop small ledges or out of water onto shore. */
  private move(animal: Animal, dt: number): void {
    const def = ANIMAL_DEFS[animal.kind];
    moveMob(this.world, animal, dt, def.width, def.height);

    const inWater = mobWaterInfo(this.world, animal.position, def.height) !== null;
    if (
      animal.blocked &&
      (animal.onGround || inWater) &&
      animal.jumpCooldown <= 0
    ) {
      // Jumping out of water needs sufficient impulse to clear the shore bank
      animal.velocity.y = inWater ? MOB_WATER_JUMP_SPEED : MOB_JUMP_SPEED;
      if (inWater) {
        // Forward lunge onto shore so horizontal momentum isn't lost on collision
        animal.velocity.x += Math.sin(animal.heading) * 1.6;
        animal.velocity.z += Math.cos(animal.heading) * 1.6;
      }
      animal.jumpCooldown = 0.8;
      animal.onGround = false;
    }
  }

  /** Scan nearby columns for dry land so swimming animals can steer toward shore. */
  private findNearbyShore(animal: Animal): number | null {
    const x = Math.floor(animal.position.x);
    const y = Math.floor(animal.position.y);
    const z = Math.floor(animal.position.z);

    for (let r = 1; r <= 8; r++) {
      for (let dx = -r; dx <= r; dx++) {
        for (let dz = -r; dz <= r; dz++) {
          if (Math.abs(dx) !== r && Math.abs(dz) !== r) continue;
          const sx = x + dx;
          const sz = z + dz;
          if (!this.isLoaded(sx, sz)) continue;

          for (let sy = y - 1; sy <= y + 2; sy++) {
            const block = this.world.peekBlock(sx, sy, sz);
            const above = this.world.peekBlock(sx, sy + 1, sz);
            if (isSolid(block) && (above === AIR || !isSolid(above))) {
              return Math.atan2(dx, dz);
            }
          }
        }
      }
    }
    return null;
  }

  /** Highest grass block in a loaded column, plus one for the feet position. */
  private findGrassColumn(wx: number, wz: number): number | null {
    if (!this.isLoaded(wx, wz)) return null;
    for (let y = WORLD_HEIGHT - 1; y >= 1; y--) {
      const block = this.world.peekBlock(wx, y, wz);
      if (block === AIR || block === WATER) continue;
      if (block !== GRASS) return null;
      if (this.world.peekBlock(wx, y + 1, wz) !== AIR) return null;
      if (this.world.peekBlock(wx, y + 2, wz) !== AIR) return null;
      return y + 1;
    }
    return null;
  }

  private isLoaded(x: number, z: number): boolean {
    return isMobLoaded(this.world, x, z);
  }

  private randomKind(): AnimalKind {
    return ANIMAL_KINDS[Math.floor(this.rand() * ANIMAL_KINDS.length)];
  }
}
